import React, { useState } from "react";
import { X, Check, ArrowUpRight } from "lucide-react";

/**
 * Lead capture modal — collects name, phone, email, city and state
 * before redirecting the user to the relevant catalog / consultation.
 */
export default function LeadCaptureModal({ open, onClose, title, subtitle, eyebrow = "GET STARTED" }) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", state: "" });

  if (!open) return null;

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock: store in localStorage as a lightweight CRM stand-in
    try {
      const leads = JSON.parse(localStorage.getItem("dplus_leads") || "[]");
      leads.push({ ...form, topic: title, at: new Date().toISOString() });
      localStorage.setItem("dplus_leads", JSON.stringify(leads));
    } catch (_) {
      // ignore
    }
    setSubmitted(true);
  };

  const close = () => {
    setSubmitted(false);
    setForm({ name: "", phone: "", email: "", city: "", state: "" });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 animate-[fadeIn_0.25s_ease-out]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[560px] overflow-hidden animate-[fadeIn_0.35s_ease-out]">
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white hover:bg-neutral-100 border border-neutral-200 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-[#1a1a1a]" />
        </button>

        <div className="p-7 md:p-10">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-[#EEF3FE] flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-[#2C66E3]" />
              </div>
              <h3 className="text-2xl font-black text-[#1a1a1a] mb-2 tracking-tight">Thanks, {form.name.split(" ")[0] || "friend"}!</h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Our team will reach out within 1 business day with a tailored quote and next steps.
              </p>
              <button
                onClick={close}
                className="bg-[#1a1a1a] hover:bg-[#2C66E3] text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] font-semibold text-[#2C66E3] mb-3">{eyebrow}</p>
              <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight leading-[1.15] mb-2">{title}</h2>
              {subtitle && <p className="text-sm text-neutral-600 mb-6 leading-relaxed">{subtitle}</p>}

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-[#1a1a1a] uppercase tracking-wide mb-1.5">Your name</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-[#2C66E3] outline-none text-[15px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#1a1a1a] uppercase tracking-wide mb-1.5">Phone</label>
                    <input
                      required
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-[#2C66E3] outline-none text-[15px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1a1a1a] uppercase tracking-wide mb-1.5">Email</label>
                    <input
                      required
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@brand.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-[#2C66E3] outline-none text-[15px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#1a1a1a] uppercase tracking-wide mb-1.5">City</label>
                    <input
                      required
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      placeholder="Miami"
                      className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-[#2C66E3] outline-none text-[15px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1a1a1a] uppercase tracking-wide mb-1.5">State</label>
                    <input
                      required
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      placeholder="FL"
                      className="w-full px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-[#2C66E3] outline-none text-[15px]"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#2C66E3] hover:bg-[#1E54C4] text-white py-3.5 rounded-full font-bold text-[15px] transition-colors flex items-center justify-center gap-2 mt-2"
                >
                  Submit & get details <ArrowUpRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] text-neutral-500 leading-relaxed text-center mt-3">
                  By submitting you agree to be contacted by Let’s Print about this offer. We never sell your info.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
